/* Shared TypeScript types matching backend API contracts. */

export interface DerivedEntityRef {
  entity_type: string;
  fk_field: string;
}

export interface EntityTypeConfig {
  key: string;
  label: string;
  icon: string;
  display_template: string;
  has_parent: boolean;
  parent_entity: string | null;
  parent_fk_field?: string | null;
  display_fields: string[];
  derived_entities?: DerivedEntityRef[];
}

export interface TenantConfig {
  tenant_id: string;
  name: string;
  modules: string[];
  mop_enabled?: boolean;
  confidence_strategy: string;
  confidence_key_entities: string[];
  entity_types: EntityTypeConfig[];
}

export type MopDeliveryState = "pending" | "sent" | "error" | "invalidated";

export interface MopOutputItem {
  request_id: string;
  parent_request_id: string | null;
  delivery_state: MopDeliveryState;
  created_at: string;
  sent_at: string | null;
  attempts: number;
  last_error: string | null;
  codice_edificio: string | null;
  descrizione_edificio: string | null;
  codice_impianto: string | null;
  descrizione_impianto: string | null;
  operazione: string | null;
  motivo: string | null;
  subject: string | null;
  sender: string | null;
  received_at: string | null;
  mop_codes: string[];
}

export interface MopListResponse {
  items: MopOutputItem[];
  total: number;
  counts: Record<MopDeliveryState, number>;
  limit: number;
  offset: number;
}

export interface MopActivity {
  codiceManutenzione: string;
  obiettivo: string | null;
  codiceUnivoco: string | null;
  tipoComponente: string | null;
  componente: string | null;
  marca: string | null;
  modello: string | null;
}

export interface MopOutputDetail extends MopOutputItem {
  payload: {
    schemaVersion: number;
    type: "mop";
    correlationId: string;
    codiceAzienda: string;
    cliente: { codice: string | null; ragioneSociale: string | null };
    richiedente: Record<string, unknown>;
    priorita: string | null;
    mopRiferimenti: Array<{
      codiceMop: string;
      idContratto: string | number | null;
      servizio: number | null;
      servizioDescr: string | null;
      dataMop: string | null;
      stato: number | null;
      attivita: MopActivity[];
    }>;
    richiesta: {
      messageId: string;
      oggetto: string;
      mittente: string;
      dataOriginale: string | null;
      ricevutaIl: string;
      testo: string;
      numeroRiferimento: string | null;
      allegati: Array<{ nome: string; tipo: string }>;
    };
  };
}

export interface ConfidenceConfig {
  confidence_strategy: string;
  confidence_key_entities: string[];
  confidence_threshold_auto_approve: number;
}

export interface ConfidencePreview {
  total_requests: number;
  would_auto_approve: number;
  would_need_review: number;
  auto_approved_with_corrections: number;
  period_days: number;
  threshold: number;
}

export interface EntityMatch {
  best: {
    entity: { pk: string; fields: Record<string, unknown> } | null;
    display_label: string;
    score: number;
    method: string;
  } | null;
  alternatives: Array<{
    entity: { pk: string; fields: Record<string, unknown> };
    metadata?: { review_reason?: string };
    display_label: string;
    score: number;
    method: string;
  }>;
  needs_review: boolean;
  confidence?: number;
  match_method?: string;
  entity_type?: string;
}

export interface DerivedEntityData {
  pk: string;
  display_label: string;
  source_entity?: string;
  source_field?: string;
}

export interface TicketMatch {
  ticket_index: number;
  entity_matches: Record<string, EntityMatch>;
  overall_confidence: number;
  derived?: Record<string, DerivedEntityData>;
  children_counts?: Record<string, number>;
}

export interface SenderInfo {
  name?: string;
  email?: string;
  phone?: string;
}

export interface IncomingData {
  sender?: SenderInfo;
  subject?: string;
  body?: string;
  body_html?: string;
  channel?: string;
  received_at?: string;
  attachments?: Array<{
    filename: string;
    content_type: string;
    size_bytes: number;
  }>;
  /* Metadati della richiesta in ingresso. È il path autoritativo dell'origine
     (`incoming_data->metadata->>source`), lo stesso su cui filtra il backend
     con `origin=conversation`. */
  metadata?: Record<string, unknown>;
}

export interface CorrectionItem {
  ticket_index: number;
  field_corrected: string;
  original_value: Record<string, unknown> | null;
  corrected_value: Record<string, unknown> | null;
  correction_category: string;
  correction_categories?: string[];
  correction_note?: string | null;
}

export interface CorrectionRecord extends CorrectionItem {
  id: string;
  request_id: string;
  operator_id: string;
  created_at: string;
}

/** Perché la pipeline ha scartato la pratica, già reso leggibile dal backend.
 *
 * `null` quando la pratica non è scartata **oppure** quando l'ha scartata un
 * operatore: in quel caso il motivo è la sua nota, che vive in `audit_log`
 * insieme al suo nome. Vedi `src/services/discard_reasons.py`. */
export interface PipelineDiscardInfo {
  key: string;
  label: string;
  /** I segnali (espressioni regolari) che hanno fatto scattare il filtro. */
  detail?: string | null;
  /** `false` quando il motivo non è una famiglia nota: si mostra il testo così com'è. */
  recognised: boolean;
}

export interface ProcessingRequest {
  id: string;
  tenant_id: string;
  channel: string;
  message_id: string;
  status: string;
  incoming_data: IncomingData | null;
  classification: Record<string, unknown> | null;
  extraction: Record<string, unknown> | null;
  analyses: Record<string, unknown> | null;
  matches: TicketMatch[] | null;
  output_data: Record<string, unknown> | null;
  /** Testo libero scritto dall'operatore in approvazione manuale, distinto da
   * `note` (LLM) e da `approval_note` (audit) — finisce nell'output come
   * `notaOperatore`. Handoff 2026-07-17. */
  nota_operatore?: string | null;
  error_message: string | null;
  created_at: string;
  updated_at: string;
  corrections?: CorrectionRecord[];
  /** Enriched by BE from extraction.metadata — top-level convenience fields */
  original_sender?: string | null;
  original_subject?: string | null;
  original_date?: string | null;
  /** Review flow: previous matches before reprocess, lock info */
  previous_matches?: TicketMatch[] | null;
  locked_by?: string | null;
  locked_by_name?: string | null;
  locked_at?: string | null;
  /** Multi-ticket: una stessa email che genera N analisi viene splittata in N record.
   * parent_request_id punta al primo record della serie (self per ticket_index=0).
   * total_tickets > 1 indica che esistono record fratelli. */
  parent_request_id?: string | null;
  ticket_index?: number;
  total_tickets?: number;
  /** Motivo dello scarto automatico, normalizzato. Valorizzato solo su
   *  `status='discarded'` e solo quando non c'è stato un operatore. */
  pipeline_discard?: PipelineDiscardInfo | null;
}

/** Riassunto di un ticket sibling (stessa email, ticket_index diverso). */
export interface SiblingTicket {
  id: string;
  status: string;
  ticket_index: number;
  total_tickets: number;
  analyses: Record<string, unknown> | null;
  matches: TicketMatch[] | null;
  created_at: string;
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  size: number;
}

// ── Statistiche: contratto condiviso fra verticali ──────────
// Rispecchia src/services/stats/contract.py. Un blocco non calcolabile per un
// flusso arriva con `available: false` e un `reason` da mostrare al posto del
// numero: uno zero si leggerebbe come "nessun caso" invece di "metrica
// inesistente".

/** Flussi per cui esiste una spec statistiche lato backend. */
export type StatsFlow = "rdi" | "ddt";

/** Filtro di lista che riproduce esattamente il numero a cui è agganciato. */
export interface StatsDrill {
  flow: string;
  /** Segmento del numero. Non è un query param: su RDI è una rotta (`/allarmi`,
   *  `/hyper`) e il sotto-tipo arriva alla lista come prop del componente, non
   *  dall'URL. La mappa segmento → rotta vive nel frontend, vedi `statsRoutes`. */
  segment?: string | null;
  /** Query param dell'URL della lista: il backend garantisce che siano applicabili. */
  params: Record<string, string>;
}

export interface StatsAvailability {
  available: boolean;
  reason?: string | null;
}

export interface StatsPeriod {
  days: number;
  timezone: string;
  start: string;
  end: string;
  prev_start: string;
  prev_end: string;
  /** true solo con la finestra "Dall'inizio" (`since=start`). Opzionale:
   *  assente su un backend non ancora aggiornato, la pagina si comporta come
   *  oggi. */
  is_full_history?: boolean;
  /** Prima pratica della popolazione del flusso; null se non ce ne sono
   *  ancora (o su un backend non ancora aggiornato). */
  first_at?: string | null;
  /** false quando non c'è un periodo precedente con cui confrontare: ogni
   *  delta va letto "—", non 0%. Assente = comportarsi come `true`. */
  has_previous?: boolean;
}

/** Un numero e il suo omologo nel periodo precedente. `delta_pct` è null quando `previous` è 0. */
export interface StatsComparison {
  current: number;
  previous: number;
  delta_pct?: number | null;
}

/** `label` è assente per gli stati: usare il registry condiviso in `lib/status.tsx`. */
export interface StatsCounter {
  key: string;
  value: number;
  label?: string | null;
  drill?: StatsDrill | null;
}

export interface StatsVolumeBlock extends StatsAvailability {
  ingested: StatsComparison;
  by_status: StatsCounter[];
}

export interface StatsAutomationTier {
  key: "automatic" | "confirmed" | "corrected" | string;
  label: string;
  count: number;
  pct?: number | null;
  drill?: StatsDrill | null;
  availability: StatsAvailability;
}

/** Una scelta possibile per la select "su quale dimensione ragiona il riquadro".
 *
 * `description` esiste perché l'etichetta da sola mente: "Motivo" non dice che
 * una riscrittura redazionale del campo non è necessariamente un errore.
 */
export interface CorrectionDimensionOption {
  key: string;
  label: string;
  description: string;
}

export interface StatsAutomationBlock extends StatsAvailability {
  approved_total: StatsComparison;
  tiers: StatsAutomationTier[];
  /** Dimensione applicata dal server (default `overall`). Opzionale: un server
      non aggiornato non la manda e la pagina deve restare quella di prima. */
  dimension?: string;
  /** Opzioni per la select; vuoto (o assente) = il flusso non le supporta e la
      select non va mostrata affatto. */
  dimensions?: CorrectionDimensionOption[];
  /** Frase da stampare dopo "è stato ...": cambia con la dimensione, quindi
      arriva dal server invece di essere cablata qui. */
  untouched_label?: string;
  /** % approvate senza alcuna correzione: "quanto lavoro ho risparmiato sulle pratiche buone". */
  untouched_rate?: number | null;
  untouched_rate_previous?: number | null;
  /** Pratiche scartate perché il sistema aveva sbagliato: escono dal conto di `untouched_rate`. */
  system_failures_discarded: number;
  /** % su (approvate + fallimenti scartati): "quanto spesso il sistema ci prende". */
  quality_rate?: number | null;
  quality_rate_availability: StatsAvailability;
}

export interface StatsCycleTimeBlock extends StatsAvailability {
  sample: number;
  p50_seconds?: number | null;
  p90_seconds?: number | null;
  p50_seconds_previous?: number | null;
}

export interface StatsDailyPoint {
  date: string;
  ingested: number;
  approved: number;
  untouched: number;
  /** null nei giorni senza approvazioni: uno zero disegnerebbe un crollo dove non si è lavorato. */
  untouched_rate?: number | null;
}

export interface StatsAgeBucket {
  key: string;
  label: string;
  value: number;
  drill?: StatsDrill | null;
}

export interface StatsBacklogBlock extends StatsAvailability {
  /** Istantaneo: questo blocco ignora il periodo. */
  as_of: string;
  open_total: number;
  oldest_arrived_at?: string | null;
  age_buckets: StatsAgeBucket[];
  broken: StatsCounter[];
  stale_locks: number;
  stale_lock_threshold_hours: number;
}

export interface StatsHotspot {
  field: string;
  label: string;
  /** Pratiche distinte, non righe di correzione. */
  practices: number;
  pct_of_approved?: number | null;
  drill?: StatsDrill | null;
}

export interface StatsHotspotBlock extends StatsAvailability {
  approved_in_period: number;
  fields: StatsHotspot[];
  categories: StatsCounter[];
}

export interface StatsDiscardBlock extends StatsAvailability {
  total: StatsComparison;
  automatic: StatsCounter[];
  automatic_availability: StatsAvailability;
  /** Per categoria selezionata dall'operatore: inaffidabile, vedi `families`. */
  by_operator: StatsCounter[];
  by_operator_availability: StatsAvailability;
  /** Per famiglia ricavata dalla nota scritta: è qui che sta la verità. */
  families: StatsCounter[];
  families_availability: StatsAvailability;
  /** Scarti che sono fallimenti del sistema, non email fuori perimetro. */
  system_failures: number;
  /** Note non ancora classificate: distinte da quelle classificate come ambigue. */
  unclassified_notes: number;
}

export interface StatsOutputQueueBlock extends StatsAvailability {
  as_of: string;
  not_picked_up: number;
  oldest_sent_at?: string | null;
  drill?: StatsDrill | null;
}

export interface StatsProjectionBlock extends StatsAvailability {
  /** Il dato osservato: approvate senza che l'operatore modificasse nulla. Sono
   *  per definizione quelle che l'invio automatico avrebbe potuto spedire. */
  could_have_been_automatic: number;
  auto_rate?: number | null;
  needed_correction: number;
  /** Fatto misurato: pratiche in cui il sistema ha sbagliato, secondo la nota
   *  dell'operatore che le ha scartate.
   *
   *  Non esiste un `error_rate` dell'automazione, e l'assenza è deliberata:
   *  dedurlo dal fatto che oggi la confidenza è piatta a 0,90 descriverebbe lo
   *  stato del punteggio, non quanto il sistema sbagli. Serve una ground truth
   *  (`docs/GROUND-TRUTH-CASADEI-RDI-ANALYSIS.md`). */
  wrong_and_discarded: number;
  /** Quanti valori distinti assume la confidenza: con pochissimi, una soglia non
   *  è una manopola ma un interruttore. */
  confidence_distinct_values: number;
  confidence_discriminates: boolean;
  threshold?: number | null;
}

/** Una voce di una ripartizione per entità.
 *
 * `corrected` conta le pratiche della voce con almeno **una** correzione manuale,
 * qualunque campo — non una correzione dell'entità stessa. Su RDI la scelta
 * dell'amministratore non scrive `processing_corrections`, quindi la quota va letta
 * come «su questa voce l'operatore ha dovuto metterci le mani», non «questa voce
 * era sbagliata».
 */
export interface StatsEntityItem {
  key: string;
  label: string;
  practices: number;
  corrected: number;
  corrected_pct?: number | null;
  drill?: StatsDrill | null;
}

/** Ripartizione per una entità (edificio, fornitore, amministratore).
 *
 * `distinct_values` e `other_practices` esistono perché la classifica è troncata:
 * senza, dieci voci si leggono come l'elenco completo.
 */
export interface StatsEntityDimension extends StatsAvailability {
  key: string;
  label: string;
  practices_in_period: number;
  distinct_values: number;
  items: StatsEntityItem[];
  other_practices: number;
}

/** Righe tenute fuori da tutta la pagina, dichiarate invece che sottratte.
 *
 * Non è una metrica e non ha un riquadro: è la riga che spiega perché il totale è
 * 102 dove il database ne ha 194. Senza, l'esclusione è indistinguibile da un bug.
 */
export interface StatsExcludedPopulation {
  label: string;
  excluded: number;
  reason: string;
}

export interface StatsSegmentCount {
  key: string;
  label: string;
  ingested: number;
  open_now: number;
  drill?: StatsDrill | null;
}

export interface StatsOverview {
  flow: StatsFlow;
  flow_label: string;
  tenant_id: string;
  period: StatsPeriod;
  /** Segmento attivo; `null` significa "tutti insieme". */
  segment?: string | null;
  segment_label?: string | null;
  segments: StatsSegmentCount[];
  volume: StatsVolumeBlock;
  automation: StatsAutomationBlock;
  projection: StatsProjectionBlock;
  cycle_time: StatsCycleTimeBlock;
  daily: StatsDailyPoint[];
  backlog: StatsBacklogBlock;
  hotspots: StatsHotspotBlock;
  discards: StatsDiscardBlock;
  output_queue: StatsOutputQueueBlock;
  /** Ripartizioni per entità: vuoto quando il flusso non ne dichiara alcuna. */
  entities: StatsEntityDimension[];
  /** Cosa questa pagina non sta guardando. `null` = la popolazione è tutta la tabella. */
  excluded?: StatsExcludedPopulation | null;
}

export interface EntitySearchResult {
  pk: string;
  label: string;
  fields: Record<string, unknown>;
  score: number;
}

export type CorrectionCategory =
  | "estrazione_errata"
  | "match_errato"
  | "dato_mancante"
  | "dato_obsoleto"
  | "ambiguita";

export const CORRECTION_CATEGORIES: { value: CorrectionCategory; label: string }[] = [
  { value: "estrazione_errata", label: "Estrazione errata" },
  { value: "match_errato", label: "Match errato" },
  { value: "dato_mancante", label: "Dato mancante" },
  { value: "dato_obsoleto", label: "Dato obsoleto" },
  { value: "ambiguita", label: "Ambiguità" },
];

// ── Review Flow: feedback categories ───────────────────────

export const ENTITY_FEEDBACK_CATEGORIES: { value: string; label: string }[] = [
  { value: "match_errato", label: "Il sistema ha sbagliato" },
  { value: "match_mancante", label: "Il sistema non ha trovato nulla" },
  { value: "match_ambiguo", label: "C'erano più candidati simili" },
  { value: "dati_incompleti", label: "Dati insufficienti nell'email" },
  { value: "anagrafica_errata", label: "Dati errati/vecchi in anagrafica" },
];

export const REJECT_CATEGORIES: { value: string; label: string }[] = [
  { value: "non_pertinente", label: "Non è una richiesta di intervento" },
  { value: "duplicato", label: "Già gestita / duplicato" },
  { value: "info_insufficienti", label: "Impossibile capire cosa serve" },
  { value: "test", label: "Email di test / prova" },
];

// ── Audit Log ──────────────────────────────────────────────

/** Flusso di un evento di audit. Assente lato filtro = tutti i flussi (righe
 *  senza flusso comprese); `none` = solo le righe senza flusso. `piattaforma`
 *  non è un modulo di tenant: eventi trasversali (magic link, credenziali di
 *  canale, ricalcolo costi) — visibili solo al platform_admin. */
export type AuditFlow =
  | "rdi"
  | "ddt"
  | "bsc"
  | "deleghe"
  | "rinascente"
  | "conversazioni"
  | "piattaforma";

export type AuditFlowFilter = AuditFlow | "none";

export interface AuditEntry {
  id: string;
  /** FK a processing_requests (RDI/DDT). Null per le azioni BSC, che usano
   *  bsc_practice_id (vedi migration 040). */
  request_id: string | null;
  /** Riferimento alla pratica BSC per le azioni del modulo BSC. */
  bsc_practice_id?: string | null;
  operator_id?: string | null;
  operator_name?: string | null;
  action: string;
  details?: Record<string, unknown> | null;
  feedback_categories?: string[] | null;
  feedback_note?: string | null;
  lesson_learned?: string | null;
  created_at: string;
  /** Tenant a cui appartiene la riga. Presente solo con l'estensione
   *  multi-tenant del registro attività (vedi AGENTS.md — non ancora
   *  presente lato backend al momento di questo pacchetto). */
  tenant_id?: string;
  /** Flusso di provenienza dell'evento. */
  flow?: AuditFlow | null;
  /** Tipo del soggetto a cui si riferisce l'evento, per costruire il link
   *  profondo (vedi `subjectDeepLink` in `lib/auditFlows.ts`). */
  subject_type?: string | null;
  /** ID del soggetto (pratica RDI/DDT/BSC/Rinascente, delega, sessione di
   *  conversazione, ...). */
  subject_id?: string | null;
}

/** Risposta di GET /api/v1/audit: come PaginatedResponse<AuditEntry>, più
 *  `tenant_scope` — "*" se il platform_admin ha chiesto tutti i tenant,
 *  altrimenti l'id del tenant effettivamente mostrato. Va usato per decidere
 *  se mostrare la colonna tenant, invece di dedurlo dallo stato locale. */
export interface AuditListResponse extends PaginatedResponse<AuditEntry> {
  tenant_scope: string;
}

// ── Lock ───────────────────────────────────────────────────

export interface LockInfo {
  locked: boolean;
  locked_by?: string | null;
  operator_name?: string | null;
  locked_at?: string | null;
}

// ── Queue & Blob ───────────────────────────────────────────

export interface QueueSummary {
  mail_path: string;
  request_id: string;
  codice_impianto: string;
  tipo_impianto: string;
  edificio: string;
  indirizzo: string;
  chiamante: string;
  confidence_impianto: number | null;
  confidence_chiamante: number | null;
  motivo: string;
  priorita: string;
  tipologia: string;
  note: string;
  nota_operatore: string | null;
  data_origine: string;
  validation_status: string;
}

// Filtro stato coda output (4 stati, coda-in-lavorazione): "prelevato" =
// messaggio non piu' in coda; una semplice lettura del consumer NON marca
// prelevato, lo mette "in lavorazione" (in lease) finche' non conferma con
// la delete. Alias legacy `pending_only` gestito lato backend.
export type QueueStatus = "all" | "pending" | "in_lease" | "picked_up";

// Stato per-messaggio, derivato server-side da queue_picked_up_at /
// queue_in_lease_since. Opzionale sul tipo perche' un server non ancora
// aggiornato non lo manda affatto (vedi QueueMessage.state).
export type QueueItemState = "pending" | "in_lease" | "picked_up";

// Conteggi per stato della risposta lista (coda-in-lavorazione): permettono
// al filtro di mostrare "In lavorazione (N)" senza una query separata.
// Opzionale: assente su un server vecchio.
export interface QueueCounts {
  all: number;
  pending: number;
  in_lease: number;
  picked_up: number;
}

export interface QueueMessage {
  // message_id (punto 4, 2026-07-14): ora l'id della processing_request
  // (stabile, da DB), non piu' il GUID effimero del messaggio Azure.
  message_id: string;
  inserted_on: string | null;
  // picked_up_at: valorizzato dal reconciler periodico quando il messaggio
  // non e' piu' presente in coda (prelevato dal consumer esterno del
  // cliente, o rimosso manualmente) — l'elemento resta comunque in lista.
  picked_up_at: string | null;
  // in_lease_since: valorizzato quando il consumer del cliente ha fatto
  // receive del messaggio e lo tiene in lease (invisibile in coda Azure,
  // ma non ancora confermato con una delete). Opzionale: un server vecchio
  // non lo manda.
  in_lease_since?: string | null;
  // Stato normalizzato lato server: "pending" | "in_lease" | "picked_up".
  // Opzionale per lo stesso motivo di in_lease_since.
  state?: QueueItemState;
  content: Record<string, unknown>;
  summary: QueueSummary;
}

export interface QueueMessagesResponse {
  queue_name: string;
  message_count: number;
  messages: QueueMessage[];
  total: number;
  page: number;
  size: number;
  // Conteggi per stato, per popolare il filtro. Opzionale: assente su un
  // server vecchio, la pagina si comporta come prima (nessuna voce vuota).
  counts?: QueueCounts;
  // true quando il peek della coda Azure non ha potuto enumerare tutti i
  // messaggi (oltre la soglia): sopra quella soglia lo stato "in lavorazione"
  // per singolo messaggio non e' determinabile e tutto resta "in coda".
  // Opzionale/assente = nessun avviso.
  peek_incomplete?: boolean;
}

// ── Coda DDT (Item 1, 2026-07-14) — stessa Azure Storage, coda separata ──────

export interface DdtQueueSummary {
  numero_ddt: string;
  data_ora: string;
  ordine_acquisto: string;
  fornitore_testo: string;
  fornitore_codice: string;
  n_righe: number;
  commesse_riferite: string[];
  nota_operatore: string | null;
}

export interface DdtQueueMessage {
  // message_id (punto 4, 2026-07-14): ora l'id della ddt_practices (stabile,
  // da DB), non piu' il GUID effimero del messaggio Azure.
  message_id: string;
  inserted_on: string | null;
  // picked_up_at: valorizzato dal reconciler periodico quando il messaggio
  // non e' piu' presente in coda — l'elemento resta comunque in lista.
  picked_up_at: string | null;
  // Vedi QueueMessage: stessi campi, stesso significato, coda DDT.
  in_lease_since?: string | null;
  state?: QueueItemState;
  content: Record<string, unknown>;
  summary: DdtQueueSummary;
}

export interface DdtQueueMessagesResponse {
  queue_name: string;
  message_count: number;
  messages: DdtQueueMessage[];
  total: number;
  page: number;
  size: number;
  // Vedi QueueMessagesResponse: stesso significato, coda DDT.
  counts?: QueueCounts;
  peek_incomplete?: boolean;
}

export interface BlobInfo {
  name: string;
  size: number;
  last_modified: string | null;
  content_type: string | null;
}

export interface BlobGroup {
  mail_path: string;
  blobs: BlobInfo[];
  total_size: number;
  created: string | null;
}

export interface BlobListResponse {
  container_name: string;
  groups: BlobGroup[];
  total_groups: number;
  page: number;
  size: number;
}

// ── Flow config types ──────────────────────────────────────

export interface FlowSummary {
  flow_id: string;
  label: string;
  enabled: boolean;
  mailboxes: string[];
  sections: Record<string, boolean>;
}

export interface FlowConfidence {
  strategy: string;
  key_entities: string[];
  threshold_auto_approve: number;
}

export interface FlowMailbox {
  email: string;
  polling_interval: number;
  folder: string;
  enabled: boolean;
  forwarder_addresses?: string[];
}

export interface FlowDestination {
  type: string;
  enabled: boolean;
}

export interface PreAnalysisRule {
  sub_type: string;
  enabled: boolean;
  params: Record<string, unknown>;
}

export interface FlowConfig {
  flow_id: string;
  label: string;
  confidence: FlowConfidence;
  input: { mailboxes: FlowMailbox[] };
  output: { format: string; destinations: FlowDestination[] };
  matching: { entity_types: string[]; cascades: Record<string, unknown[]> };
  extraction: {
    entity_types_involved: string[];
    classifier_rules: Record<string, unknown>[];
    prompt_template: string;
  };
  pre_analysis_rules?: PreAnalysisRule[];
}

export interface ConfidencePreview {
  total_requests: number;
  would_auto_approve: number;
  would_need_review: number;
  auto_approved_with_corrections: number;
  period_days: number;
  threshold: number;
  flow_id: string;
}

// ── Admin types ────────────────────────────────────────────

export interface AdminTenant {
  tenant_id: string;
  name: string;
  modules: string[];
  created_at: string;
}

// Stato per-canale dell'agente conversazionale, in forma sicura: SOLO il
// flag `enabled`, mai credenziali (`access_token`, `phone_number_id`, ...) —
// vedi `GET /api/v1/admin/tenants/{id}/channels` in admin_tenants.py, che
// applica lo stesso principio di `GET /api/v1/conversations/config`.
export interface TenantChannelStatus {
  enabled: boolean;
}

// Body del PATCH esteso — tutti i campi opzionali: un campo omesso resta
// invariato lato server. I campi credenziale sono write-only per
// costruzione (la GET non li restituisce mai, quindi questo tipo non ha
// un equivalente "read"): usali solo per comporre il body di una PATCH,
// mai per popolare un form con un valore esistente.
export interface TenantChannelUpdate {
  enabled?: boolean;
  access_token?: string;
  phone_number_id?: string;
  webhook_verify_token?: string;
  business_account_id?: string;
}

export interface TenantChannelsStatus {
  tenant_id: string;
  channels: Record<string, TenantChannelStatus>;
}

export interface AdminTenantDetail {
  tenant_id: string;
  config: Record<string, unknown>;
  created_at: string;
}

export interface AdminUser {
  user_id: string;
  email: string;
  first_name: string;
  last_name: string;
  display_name: string;
  state: string;
  email_verified: boolean;
  created_at: string | null;
  last_login: string | null;
  password_changed?: string | null;
  tenant_id?: string;
  roles: string[];
  metadata?: Record<string, string>;
}

/**
 * Copertura del dato aggregato: quante chiamate hanno davvero un prezzo e
 * quante contribuiscono alla latenza media. Servono a non spacciare per
 * "totale" una somma che ignora le righe senza costo.
 * Opzionali: le versioni piu' vecchie dell'API non li emettono.
 */
export interface CostCoverage {
  priced_call_count?: number;
  unpriced_call_count?: number;
  unpriced_tokens?: number;
  latency_sample_count?: number;
}

/**
 * Finestra temporale dichiarata dall'API (giorni di calendario nel fuso
 * indicato, non finestre mobili di 24h). Opzionali per lo stesso motivo.
 */
export interface CostPeriodWindow {
  period_start?: string;
  period_end?: string;
  timezone?: string;
  /** Giorni di calendario "YYYY-MM-DD" della finestra effettiva, estremi
   * inclusi: l'API li riecheggia sempre (anche quando la finestra arriva da
   * `period`), cosi' il frontend non la ricalcola. */
  date_from?: string;
  date_to?: string;
}

/**
 * Costo unitario e sua ripartizione (invariante 7 del backend).
 *
 * `ticket_count` e' il numero di **pratiche distinte con almeno una chiamata
 * LLM nella finestra**: e' il denominatore che rende confrontabili due periodi
 * (un mese piu' caro puo' solo aver lavorato piu' pratiche). Non e' il numero
 * di pratiche *nate* nella finestra.
 *
 * I due blocchi partizionano il volume e, per costruzione,
 * `ticket_cost_usd + unattributed_cost_usd === total_cost_usd`. Il costo non
 * attribuibile (oggi i turni dell'agente conversazionale, che non scrivono
 * `request_id`) **non va spalmato** sui ticket ne' nascosto: `avg_cost_per_
 * ticket_usd` ha al numeratore solo `ticket_cost_usd`.
 *
 * Come sempre qui, `null` = "non calcolabile", mai 0: zero ticket o nessuna
 * chiamata di ticket prezzata danno media `null`.
 */
/**
 * Categorie in cui si scompone il costo di un ticket (invariante 8 del
 * backend, `_CATEGORY_ORDER` in `admin_costs.py`). L'ordine di questa union e'
 * l'ordine di presentazione: dialogo -> acquisizione -> analisi ->
 * lavorazione -> altro, uguale per ogni riga, cosi' due barre si confrontano
 * a colpo d'occhio.
 */
export type CostCategory = "dialogo" | "acquisizione" | "analisi" | "lavorazione" | "altro";

/**
 * Una voce della scomposizione. `cost_usd` e' nullable con il significato di
 * sempre (nessuna chiamata prezzata in quella categoria = non lo sappiamo,
 * NON zero).
 *
 * Le categorie **partizionano** le chiamate: la somma dei `cost_usd` fa il
 * `total_cost_usd` della riga che le porta. Una categoria senza nemmeno una
 * chiamata **non compare** nell'array: l'assenza vale "nessuna chiamata di
 * quella natura", non "e' costata zero".
 */
export interface CostCategoryShare {
  category: CostCategory;
  call_count: number;
  priced_call_count: number;
  cost_usd: number | null;
}

export interface CostTicketSplit {
  ticket_count: number;
  ticket_call_count: number;
  ticket_priced_call_count: number;
  ticket_tokens: number;
  ticket_cost_usd: number | null;
  avg_cost_per_ticket_usd: number | null;
  unattributed_call_count: number;
  unattributed_priced_call_count: number;
  unattributed_tokens: number;
  unattributed_cost_usd: number | null;
  /** Di cosa e' fatto `ticket_cost_usd` (non il totale della finestra: cio' che
   *  non ha un ticket non ha nemmeno una pratica di cui essere il dialogo).
   *  Somma esattamente a `ticket_cost_usd`. */
  ticket_cost_by_category: CostCategoryShare[];
}

/**
 * Una riga della classifica dei ticket piu' costosi
 * (`/admin/costs/top-requests`). Il `request_id` e' l'identita' del ticket ed
 * e' anche il link al suo dettaglio (`/requests/:id`), che e' il posto dove si
 * va a vedere *perche'* costa.
 */
export interface CostTopRequest extends CostCoverage {
  request_id: string;
  /** false = questo `request_id` non e' (piu') una pratica: `llm_calls.request_id`
   *  non e' una FK e per il flusso deleghe porta un `delegation_checks.id`. Il
   *  costo resta vero, il contesto e' `null` e non c'e' un dettaglio da aprire. */
  request_found: boolean;
  tenant_id: string | null;
  channel: string | null;
  status: string | null;
  message_id: string | null;
  /** Un messaggio email puo' portare fino a 8 ticket: senza questi due, due
   *  righe della classifica sembrerebbero la stessa pratica. */
  ticket_index: number | null;
  total_tickets: number | null;
  /** Data della PRATICA (non della chiamata): e' il riferimento con cui
   *  l'operatore la riconosce. ISO con offset, oppure null. */
  created_at: string | null;
  /** Flusso ricavato dagli step delle sue chiamate — `processing_requests` non
   *  ha una colonna "tipo". `null` = step di piu' flussi (o nessuno noto). */
  flow: string | null;
  steps: string[];
  call_count: number;
  total_tokens: number;
  total_cost_usd: number | null;
  avg_latency_ms: number | null;
  /** Di cosa e' fatto `total_cost_usd` di QUESTO ticket. Da quando il dialogo
   *  che genera una pratica ne porta il `request_id`, il totale di un ticket
   *  conversazionale comprende la conversazione: senza la scomposizione non
   *  sarebbe confrontabile con un ticket arrivato via email. Somma a
   *  `total_cost_usd`. */
  cost_by_category: CostCategoryShare[];
}

/**
 * NB: `total_cost_usd` e `avg_latency_ms` sono nullable — `null` significa
 * "non lo sappiamo" (nessuna chiamata con prezzo / con latenza registrata) e
 * NON deve essere reso come 0 in UI.
 */
export interface CostSummary extends CostCoverage, CostPeriodWindow, CostTicketSplit {
  period: string;
  tenant_id?: string | null;
  call_count: number;
  total_tokens: number;
  total_cost_usd: number | null;
  avg_latency_ms: number | null;
}

export interface CostByTenant extends CostCoverage {
  /** null = chiamate non attribuibili a un cliente (migration 059): voce "Senza cliente", non un tenant. */
  tenant_id: string | null;
  call_count: number;
  total_tokens: number;
  total_cost_usd: number | null;
  avg_latency_ms: number | null;
}

export interface CostByType extends CostCoverage {
  call_type: string;
  /** null = modello mai registrato per queste chiamate (migration 058). */
  model: string | null;
  call_count: number;
  total_tokens: number;
  total_cost_usd: number | null;
  avg_latency_ms: number | null;
}

export interface CostDaily extends CostCoverage {
  date: string;
  call_count: number;
  cost_usd: number | null;
  tokens: number;
  /** Dettaglio per cliente dello stesso giorno (sempre presente, anche filtrando
   * su un tenant: in quel caso ha una riga sola). `tenant_id: null` = non attribuito. */
  by_tenant: { tenant_id: string | null; cost_usd: number | null; call_count: number }[];
  /** Dettaglio per modello (normalizzato, senza prefisso provider) dello stesso
   * giorno — la "timeline" di quale modello e' stato usato quando. */
  by_model: { model: string | null; cost_usd: number | null; call_count: number }[];
}

/** Granularita' del bucket temporale, gia' risolta: `/timeseries` non
 * restituisce mai `"auto"` (quello e' solo un valore in richiesta).
 * `month` e' l'unita' dello storico pluriennale: oltre l'anno le settimane
 * diventano piu' di 53 barre e il confronto utile e' mese su mese. */
export type CostGranularity = "hour" | "day" | "week" | "month";

/**
 * Un bucket della serie temporale dei costi (`/admin/costs/timeseries`).
 *
 * Semantica del bucket vuoto (invariante dei costi, non un dettaglio): i
 * conteggi a 0 sono misure vere ("in quell'ora nessuna chiamata"), mentre
 * `cost_usd: null` significa "non c'e' nulla da sommare" e NON va reso come
 * 0, che si leggerebbe "gratis". `by_tenant`/`by_model` sono `[]`, non una
 * riga finta a zero.
 */
export interface CostBucket extends CostCoverage {
  /** Inizio del bucket, "YYYY-MM-DDTHH:MM" in ora locale di `timezone`,
   * naive (senza offset). Per `day`, `week` e `month` la parte orario e'
   * `T00:00` (e per `month` il giorno e' sempre il primo del mese). */
  bucket_start: string;
  /** Fine del bucket, stesso formato, estremo superiore ESCLUSIVO: coincide
   * con il `bucket_start` del bucket successivo. */
  bucket_end: string;
  /** `bucket_start[:10]`, cioe' il giorno di calendario del bucket. */
  date: string;
  call_count: number;
  /** null = nessuna chiamata prezzata nel bucket: non e' 0.0. */
  cost_usd: number | null;
  tokens: number;
  /** null = latenza non registrata (o bucket vuoto). */
  avg_latency_ms?: number | null;
  /** Dettaglio per cliente dello stesso bucket. `tenant_id: null` = non attribuito. */
  by_tenant: { tenant_id: string | null; cost_usd: number | null; call_count: number }[];
  /** Dettaglio per modello (normalizzato, senza prefisso provider) dello stesso bucket. */
  by_model: { model: string | null; cost_usd: number | null; call_count: number }[];
}

/**
 * Serie temporale dei costi: i bucket sono completi e ordinati (ogni
 * ora/giorno/settimana/mese della finestra compare esattamente una volta,
 * anche senza chiamate) e nessuno di essi sta interamente nel futuro — la
 * serie si ferma al bucket che contiene "adesso" nel fuso dichiarato.
 */
export interface CostTimeseries {
  /** Il preset richiesto, oppure `"custom"` se la finestra arriva da `date_from`/`date_to`. */
  period: string;
  /** Estremi della finestra come istanti ISO con offset. */
  period_start: string;
  period_end: string;
  /** Fuso in cui sono espressi i `bucket_start`/`bucket_end` (es. "Europe/Rome"). */
  timezone: string;
  tenant_id: string | null;
  flow: string | null;
  /** Finestra effettiva in giorni di calendario, estremi inclusi. */
  date_from: string;
  date_to: string;
  /** Sempre risolta: mai `"auto"`. */
  granularity: CostGranularity;
  buckets: CostBucket[];
}

export interface CostByStep extends CostCoverage {
  step: string;
  call_count: number;
  total_tokens: number;
  total_cost_usd: number | null;
  avg_latency_ms: number | null;
}

export interface CostRequestCall {
  id: string;
  step: string | null;
  call_type: string;
  /** null = modello mai registrato (migration 058). */
  model: string | null;
  /** I token possono mancare (es. travaso deleghe ai_steps -> llm_calls): null = non noti. */
  prompt_tokens: number | null;
  completion_tokens: number | null;
  total_tokens: number | null;
  /** null = costo mai calcolato per questa chiamata (prezzo del modello mancante). */
  cost_usd: number | null;
  /** null = latenza non registrata. */
  latency_ms: number | null;
  created_at: string;
}

/** Classifica dei ticket piu' costosi della finestra: le righe piu' i totali
 *  della finestra (gli stessi che dichiara `/summary`, cosi' la card sa dire
 *  quanti ticket NON sta mostrando e quanto costo non e' attribuibile). */
export interface CostTopRequestsResponse extends CostPeriodWindow, CostTicketSplit {
  period: string;
  tenant_id: string | null;
  flow: string | null;
  /** Quante righe al massimo porta questa risposta. */
  limit: number;
  requests: CostTopRequest[];
}

export interface CostByRequestResponse extends CostCoverage {
  request_id: string;
  total_cost_usd: number | null;
  calls: CostRequestCall[];
}

// ── Deleghe V2 (Acquirente Unico) ──────────────────────────

export interface AiStep {
  step: string;
  model: string;
  tokens: number;
  latency_ms: number;
  cost_usd: number;
}

export interface CompanyInfo {
  denominazione: string;
  vat_number?: string;
  codice_fiscale?: string;
  sede_legale?: string;
  forma_giuridica?: string;
  stato?: string;
  oggetto_sociale?: string;
  capitale_sociale?: string;
}

export type EsitoType = "OK" | "KO";
export type VerdettoCodice = "LEGITTIMATO" | "NON_LEGITTIMATO";

export interface Livello1Result {
  societa_attiva: boolean;
  in_liquidazione: boolean;
  delegante_trovato: boolean;
  nome_in_visura?: string;
  sezione_trovata?: string;
  esito: EsitoType;
  note?: string;
}

export interface Livello2Result {
  tipo_soggetto?: string;
  carica?: string;
  codice_carica?: string;
  data_nomina?: string;
  data_cessazione?: string;
  modalita_firma?: string;
  poteri_verbatim?: string;
  regola_applicata?: string;
  puo_firmare_da_solo: boolean;
  esito: EsitoType;
  note?: string;
}

export interface Livello3Result {
  oggetto_compatibile: boolean;
  non_eccede_poteri: boolean;
  regola_applicata?: string;
  esito: EsitoType;
  note?: string;
}

export interface DelegaVerdetto {
  stato: VerdettoCodice;
  reasoning?: string;
}

export interface DelegaResult {
  check_id: string;
  delegante_nome?: string;
  delegante_cognome?: string;
  delegante_email?: string;
  delegante_telefono?: string;
  company_info?: CompanyInfo;
  livello_1?: Livello1Result;
  livello_2?: Livello2Result;
  livello_3?: Livello3Result;
  verdetto: DelegaVerdetto;
  ai_steps: AiStep[];
  warnings?: string[];
}

export interface DelegaStats {
  total: number;
  completed: number;
  legittimati: number;
  non_legittimati: number;
  avg_cost_usd?: number;
}

export type FirmaStatusCode =
  | "attesa_delegante"
  | "attesa_delegato"
  | "completato"
  | "errore"
  | null;

export interface FirmaStatus {
  firma_status: FirmaStatusCode;
  firma_avviata_at?: string;
  firma_completata_at?: string;
  firma_session_delegante_id?: string;
  firma_session_delegato_id?: string;
  wa_number: string;
}

/** Riga della lista storico deleghe (GET /history). */
export interface DelegaHistoryItem {
  id: string;
  operator_id?: string;
  status: string;
  delegante_nome: string;
  delegante_cognome: string;
  delegante_email?: string;
  delegante_telefono?: string;
  delegato_nome: string;
  delegato_cognome: string;
  attivita: string;
  verdetto_stato?: VerdettoCodice;
  has_documento: boolean;
  firma_status: FirmaStatusCode;
  firma_avviata_at?: string;
  firma_completata_at?: string;
  created_at?: string;
  completed_at?: string;
}

/** Dettaglio completo di una verifica (GET /{check_id}). */
export interface DelegaCheck extends DelegaHistoryItem {
  delegante_cf?: string;
  delegante_data_nascita?: string;
  delegato_email?: string;
  delegato_telefono?: string;
  delegato_cf?: string;
  delegato_data_nascita?: string;
  verdetto_reasoning?: string;
  company_info?: CompanyInfo;
  livello_1?: Livello1Result;
  livello_2?: Livello2Result;
  livello_3?: Livello3Result;
  ai_steps?: AiStep[];
  analysis_warnings?: string[];
  documento_generato_at?: string;
  firma_session_delegante_id?: string;
  firma_session_delegato_id?: string;
  wa_number: string;
}

// ── BSC (Bill of Sale Certificate) — Frabemar ────────────────────────────

export interface BscData {
  // Bill of Lading (12 campi)
  bl_number: string | null;
  vessel_name: string | null;
  voyage_number: string | null;
  pol: string | null;
  pol_country: string | null;
  pod: string | null;
  pod_country: string | null;
  container_number: string | null;
  seal_number: string | null;
  packages_number: string | null;
  gross_weight: string | null;
  goods_description: string | null;
  // Fattura Commerciale (14 campi)
  invoice_number: string | null;
  invoice_date: string | null;
  invoice_total: number | null;
  invoice_currency: string | null;
  importer_name: string | null;
  importer_address: string | null;
  importer_city: string | null;
  importer_country: string | null;
  importer_vat: string | null;
  exporter_name: string | null;
  exporter_address: string | null;
  exporter_city: string | null;
  exporter_country: string | null;
  net_weight: string | null;
  // CIF / Condizioni di trasporto (Incoterm)
  terms_of_delivery: string | null; // "CIF", "FOB", "EXW", "CFR", ...
  freight: number | null;           // Quota nolo
  insurance: number | null;         // Quota assicurazione
  // Packing List (5 campi, Phase 5) — NON conteggiano in completeness
  pl_number: string | null;
  pl_total_packages: string | null;
  pl_total_gross_weight: string | null;
  pl_total_net_weight: string | null;
  pl_marks_numbers: string | null;
  // Dichiarazione doganale EX1 (4 campi, Phase 5) — NON conteggiano in completeness
  ex1_mrn: string | null;
  ex1_declaration_date: string | null;
  ex1_customs_office: string | null;
  ex1_reference: string | null;
  // Cliente (chi scrive l'email a Frabemar, dalla firma) — NON conteggiano in
  // completeness e NON fanno parte del JSON di output verso Frabemar.
  client_name: string | null;
  client_email: string | null;
  // Da dove viene l'indirizzo: chain (intestazione del primo invio) | signature
  // | body | sender | llm. Vedi ClientSourceChip.
  client_email_source: string | null;
}

// Consistenza FOB estratto vs derivato (invoice_total - freight - insurance).
// null se manca il FOB estratto o il totale fattura (nulla da confrontare).
export interface BscFobConsistency {
  extracted: number;
  derived: number;
  delta: number;
  mismatch: boolean;
}

// Le due soglie HANJA (Fase A/B, 2026-07-23) — indicatore PRIMARIO mostrato
// all'operatore, sostituisce la vecchia metrica a 26 campi core. "minimo" =
// bl_number presente (a livello di BscData; user/fileBl si verificano
// altrove). "completo" = 28 campi (27 scalari + regola "almeno un tipo di
// trasporto completo", vedi BscData.hanja_complete_status() lato BE).
export interface BscHanjaReadiness {
  minimal_ready: boolean;
  complete_non_null: number;
  complete_total: number;
  complete_missing: string[];
  fob_consistency: BscFobConsistency | null;
}

export interface BscLlmMetadata {
  model: string;
  prompt_tokens: number | null;
  completion_tokens: number | null;
  latency_ms: number;
  completeness: number;
  non_null_count: number;
  validation_errors?: string[];
  graph_result_keys?: string[];
  // {campo: valore_grezzo_estratto_da_LLM} — popolato solo per i campi
  // normalizzati automaticamente da apply_entity_corrections() (BE). Il
  // valore "normalizzato" attuale è in bsc_data[campo]; questo è l'originale
  // prima della correzione appresa.
  entity_corrections?: Record<string, string>;
  hanja?: BscHanjaReadiness;
}

export type BscStatus = "pending_documents" | "pending_review" | "approved" | "rejected" | "discarded" | "error";

export type BscDocType = "bl" | "invoice" | "packing_list" | "ex1" | "other";

// Documento caricato manualmente da dashboard (upload documenti). Vedi
// POST /bsc/{id}/documents e POST /bsc/{id}/documents/{filename}/process.
export interface BscManualDocument {
  filename: string;
  doc_type: BscDocType;
  tag: string | null; // tag libero, popolato solo per doc_type='other'
  uploaded_at: string;
  uploaded_by: string | null;
  fills_missing_slot: boolean;
  processed: boolean;
  extracted_fields: BscAdditionalField[] | null; // popolato dopo /process, solo per doc_type='other'
  updated_fields?: string[]; // campi core scritti da questo documento (usato per il revert on-delete)
}

export interface BscAdditionalField {
  label: string;
  value: string;
}

export interface BscUploadDocumentResponse {
  ok: boolean;
  filename: string;
  doc_type: BscDocType;
  fills_missing_slot: boolean;
  tag: string | null;
  text_preview: string;
}

export interface BscProcessDocumentResponse {
  ok: boolean;
  status: BscStatus;
  updated_fields: string[];
  additional_fields: BscAdditionalField[];
  completeness: number;
  non_null_count: number;
}

export interface BscDocumentTagsResponse {
  tags: string[];
}

export interface BscDeleteDocumentResponse {
  ok: boolean;
  reverted_fields: string[];
  status: BscStatus;
  has_bl: boolean;
  has_invoice: boolean;
}

// Email originale parsata dall'EML e salvata in bsc_practices.raw_emails[]
export interface BscRawEmail {
  message_id: string | null;
  subject: string | null;
  body: string | null;
  sender: string | null;
  received_at: string | null;
  attachments_text: BscAttachmentText[];
}

// Testo estratto da ogni allegato PDF via OCR (Mistral)
export interface BscAttachmentText {
  filename: string;
  text: string;
}

export type BscSortKey =
  | "date_desc" | "date_asc"
  | "bl_asc" | "bl_desc"
  | "invoice_asc" | "invoice_desc"
  | "completeness_desc" | "completeness_asc"
  | "country_asc" | "country_desc";

export interface BscSuggestion {
  id: string;
  bl_number: string | null;
  invoice_number: string | null;
  vessel_name: string | null;
  pod: string | null;
  importer_name: string | null;
  exporter_name: string | null;
  client_name: string | null;
  status: BscStatus;
}

export interface BscStatusCount {
  total: number;
  unread: number;
}

export interface BscCount {
  pending_review: BscStatusCount;
  pending_documents: BscStatusCount;
  approved: BscStatusCount;
  rejected: BscStatusCount;
  discarded: BscStatusCount;
  error: BscStatusCount;
  /** Aggregato su tutti gli stati (filtro "Tutte"). */
  all: BscStatusCount;
}

export interface BscPractice {
  id: string;
  tenant_id: string;
  message_ids: string[];
  practice_tag: string | null;
  status: BscStatus;
  has_bl: boolean;
  has_invoice: boolean;
  bsc_data: BscData | null;
  llm_metadata: BscLlmMetadata | null;
  corrections: Partial<BscData>;
  operator_id: string | null;
  operator_note: string | null;
  approved_at: string | null;
  rejected_at: string | null;
  discarded_at?: string | null;
  output_path: string | null;
  locked_by: string | null;
  locked_by_name: string | null;
  locked_at: string | null;
  error_message: string | null;
  raw_emails?: BscRawEmail[];       // popolato solo nel GET /{id}
  ocr_text?: string | null;          // popolato solo nel GET /{id}
  pdf_files?: Record<string, { path: string; doc_type: BscDocType } | string>; // nuovo schema: {filename: {path, doc_type}} — compat: vecchio era {filename: path}
  manual_documents?: BscManualDocument[]; // documenti caricati manualmente da dashboard (upload documenti)
  reconciliation_key?: string | null;
  is_duplicate?: boolean;
  duplicate_of?: string | null;
  /** Codice ISO 3166-1 alpha-2 calcolato dal BE da bsc_data.importer_country
   * (stessa mappa usata per filtro/sort server-side). Puo' essere null se il
   * paese grezzo non e' riconosciuto — il FE fa comunque fallback locale.
   * Phase 3: il display e' solo alpha-2 (es. GN, GA), non piu' alpha-3. */
  importer_country_iso2?: string | null;
  /** Phase 1: paese di destinazione RISOLTO con priorita' BL (pod_country) >
   *  Invoice (importer_country). Il valore grezzo viene dal BE per evitare
   *  che il FE ri-derivi la priorita' client-side. */
  resolved_country?: string | null;
  /** Codice ISO 3166-1 alpha-2 del resolved_country (BE). */
  resolved_country_iso2?: string | null;
  /** 'bl' se resolved deriva dal BL (pod_country), 'invoice' se dall'importer,
   *  null se nessun paese era estraibile. */
  resolved_country_source?: "bl" | "invoice" | null;
  /** True se il paese risolto esiste ma non e' africano (mercato atteso di
   *  Frabemar). Flag di avviso, non blocca la pratica. */
  country_anomaly?: boolean | null;
  /** Data di INVIO dell'email (sentDateTime). Null per le pratiche vecchie non
   *  ancora riprocessate — in quel caso la lista mostra created_at come stima. */
  sent_at?: string | null;
  /** Stato letto/non-letto CONDIVISO di team: false = nessun operatore l'ha
   *  ancora aperta (riga in grassetto nella lista). */
  letta?: boolean;
  /** Id del certificato restituito da HANJA al primo invio. NULL finche' la
   *  pratica non e' stata inviata — e oggi resta NULL per tutte, perche'
   *  endpoint e credenziali HANJA non sono ancora disponibili. */
  hanja_certificate_id?: string | null;
  created_at: string;
  updated_at: string;
}

export interface BscListResponse {
  items: BscPractice[];
  total: number;
  page: number;
  page_size: number;
}

// ── BSC: Coda Output / Stats / Config ───────────────────────────────────────

export interface BscOutputItem {
  id: string;
  tenant_id: string;
  has_bl: boolean;
  has_invoice: boolean;
  bsc_data: Record<string, unknown> | null;
  corrections: Record<string, unknown> | null;
  output_data: Record<string, unknown> | null;
  final_data: Record<string, unknown>;
  output_path: string | null;
  operator_id: string | null;
  operator_note: string | null;
  approved_at: string | null;
  /** Id certificato HANJA; NULL finche' l'invio reale non esiste. */
  hanja_certificate_id?: string | null;
  llm_metadata: Record<string, unknown> | null;
  pdf_files?: Record<string, { path: string; doc_type: "bl" | "invoice" | "other" } | string> | null;
  completeness: number;
  created_at: string;
  updated_at: string;
  // Campi quick-access da final_data
  bl_number: string | null;
  invoice_number: string | null;
  vessel_name: string | null;
  pol: string | null;
  pod: string | null;
  pod_country: string | null;
  importer_name: string | null;
  importer_country: string | null;
  exporter_name: string | null;
  invoice_total: number | null;
  invoice_currency: string | null;
  non_null_count: number;
}

export interface BscOutputListResponse {
  items: BscOutputItem[];
  total: number;
  page: number;
  size: number;
}

export interface BscReopenResult {
  ok: boolean;
  output_purge: { queue_deleted: number; blobs_deleted: number; errors: string[] };
}

export interface BscClearResult {
  ok: boolean;
  reopened: string[];
  blobs_deleted: number;
  errors: string[];
}

export interface BscTopVessel {
  label: string;
  count: number;
  avg_completeness: number;
}

export interface BscStatsResponse {
  total: number;
  by_status: Record<string, number>;
  by_date: { date: string; count: number }[];
  top_vessels: BscTopVessel[];
  by_completeness: Record<string, number>;
  approved_today: number;
  pending_review: number;
  approved: number;
  rejected: number;
  error: number;
  period_days: number;
}

export interface BscConfig {
  use_tag_correlation: boolean;
  tag_extraction_pattern: string | null;
  output_path: string;
  completeness_warning_threshold: number;
  confidence_threshold_auto_approve: number;
}

export interface BscConfidencePreview {
  total: number;
  would_auto_approve: number;
  would_need_review: number;
  period_days: number;
  threshold: number;
}

// ── DDT (Documenti di Trasporto) — Casadei ───────────────────────────────

export interface DdtDestinazione {
  anno_riferimento?: number | null;
  routing_letter: string;            // "A" | "B" | "C" | "RES" | "M" | "default"
  quantita: number;
  // ADR 0007 (2026-07-17): tipo_riferimento/identificativo sostituiscono
  // (non additivo) i precedenti codcom/idchiamata — mutuamente esclusivi per
  // costruzione (una destinazione è O una commessa, O una chiamata, O un MOP).
  tipo_riferimento: "commessa" | "chiamata" | "mop" | null;
  identificativo: string | null;     // es. "007461" (commessa), "1714" (chiamata), "M248279" (mop)
  desmag: string;                    // "MAGAZZINO RICAMBI" per routing M
  match_method: string | null;       // "codcom_exact" | "codcom_normalizzato" | "idchiamata" | "mop" | "magazzino" | "non_risolto" | "oda_codcommessa" | "fuzzy_testo" | "commessa_principale" | "esplicito_manuale" | "idchiamata_verificato_testo" | "idchiamata_ambiguo"
  codice_edificio: string | null;    // GAP-13: edificio da match testo libero (null in tutti gli altri casi)
  // GAP-16 (2026-07-13): audit del codice grezzo dietro una destinazione non
  // risolta (match_method="non_risolto") — null in tutti gli altri casi.
  codice_raw: string | null;         // es. "OMM 7321", "COM F321"
  descrizione_raw: string | null;    // es. "CASE INN"
  // GAP-18 (2026-07-15): escalation esplicita per-destinazione — True per
  // match_method="idchiamata_verificato_testo" quando lo score fuzzy testo/
  // edificio è sotto la soglia di auto-conferma (score-dipendente, per-istanza,
  // a differenza degli altri match_method la cui escalation è un binario fisso
  // per metodo). Sempre True per "idchiamata_ambiguo". False per tutti gli altri.
  needs_escalation: boolean;
  // Score fuzzy (0.0-1.0) del match testo/edificio (GAP-18) — valorizzato solo
  // per match_method in ("idchiamata_verificato_testo", "idchiamata_ambiguo").
  edificio_score: number | null;
}

export interface DdtLineItem {
  codice_articolo: string | null;
  descrizione: string | null;
  um: string | null;                 // UM normalizzata (PZ, MT, …)
  quantita_totale: number | null;
  prezzo: number | null;
  sconto_raw: string | null;         // sconti cascata raw (es. "50,0 10,0") — contratto output, invariato
  // Item 2 (2026-07-14): stessi sconti di sconto_raw come percentuali separate
  // (es. sconto_raw="37+19" -> sconti=[37, 19]), per celle editabili singole.
  sconti: number[];
  prezzo_asteriscato: boolean;       // prezzo con "*" → da confermare/omaggio
  in_garanzia: boolean;              // riga in garanzia
  needs_escalation: boolean;         // routing implicito ambiguo
  destinazioni: DdtDestinazione[];
  // GAP-03: lookup CODALTERNATIVO via articoli.codartalt
  codart_interno: string | null;     // CODART gestionale (null se non trovato o lookup non eseguito)
  articolo_trovato: boolean | null;  // null=lookup non eseguito, true=trovato, false=non trovato
  // GAP-04: motivi escalation leggibili (lista: ogni condizione appende il proprio motivo)
  escalation_motivi: string[];  // es. ["CODALTERNATIVO non trovato: DAB103040000H", "Prezzo con asterisco (*)"]
  // GAP-07: totali e coerenza sconto
  prezzo_totale_riga: number | null;
  prezzo_netto_calcolato: number | null;
  sconto_incongruente: boolean;
  // GAP-08: prezzo e quantita da ODA (match_oda)
  prezzo_oda: number | null;
  sconto_oda: string | null;
  qty_ordinata: number | null;
  sovraconsegna: boolean;
  // GAP-11: tipo riga e ODA di riferimento
  tipo_riga: "da_oda" | "su_piazza" | "sconosciuto" | "non_in_oda";
  ordine_riga_ref: string | null;
}

export interface DdtData {
  ordine_acquisto: string | null;
  numero_ddt: string | null;
  data_ora: string | null;
  fornitore_testo: string | null;
  fornitore_pk: string | null;
  // Anagrafica fornitore da DB (ADR 0002) — snapshot congelato, sola lettura in dashboard
  fornitore_testo_ocr: string | null;
  fornitore_piva: string | null;
  fornitore_codice_fiscale: string | null;
  fornitore_indirizzo: string | null;
  fornitore_telefono: string | null;
  fornitore_fax: string | null;
  righe: DdtLineItem[];
}

export type DdtStatus = "pending_review" | "no_pdf" | "approved" | "rejected" | "error" | "reprocessing";

// Tipo documento (ADR 0005, 2026-07-16) — tag informativo, ortogonale allo
// status. "ddt" = documento di trasporto, "fattura" = fattura arrivata nella
// stessa mailbox, "non_ddt" = ne' l'uno ne' l'altro (residuo).
export type DdtDocumentType = "ddt" | "fattura" | "non_ddt";

export interface DdtPractice {
  id: string;
  tenant_id: string;
  message_id: string;
  email_subject: string | null;
  email_sender: string | null;
  email_received_at: string | null;
  pdf_filename: string | null;
  status: DdtStatus;
  document_type: DdtDocumentType;
  is_duplicate: boolean;
  duplicate_of: string | null;
  ddt_data: DdtData | null;
  fornitore_pk: string | null;
  fornitore_sim: number | null;
  llm_metadata: Record<string, unknown> | null;
  // Path dotted (es. "numero_ddt" | "righe.2.prezzo") — non un Partial<DdtData>
  // flat: le correzioni riga richiedono indici di lista (vedi DdtDetail.tsx).
  corrections: Record<string, unknown>;
  operator_id: string | null;
  operator_name: string | null;
  operator_note: string | null;
  /** Testo libero scritto dall'operatore in approvazione, distinto da
   * `operator_note` (nota di audit) — finisce nell'output come `notaOperatore`.
   * Handoff 2026-07-17. */
  nota_operatore: string | null;
  approved_at: string | null;
  rejected_at: string | null;
  output_sent_at: string | null;
  locked_by: string | null;
  locked_by_name: string | null;
  locked_at: string | null;
  error_message: string | null;
  created_at: string;
  updated_at: string;
  // Multi-split metadata (migration 023)
  page_start: number | null;
  page_end: number | null;
  split_index: number;
  total_splits: number;
}

export interface DdtListResponse {
  items: DdtPractice[];
  total: number;
  page: number;
  page_size: number;
}

// ── Filtri e sort per listDdtPractices (specchia la dashboard RDI) ──────

export type DdtSortKey =
  | "date_desc" | "date_asc"
  | "id_desc" | "id_asc"
  | "numero_ddt_desc" | "numero_ddt_asc"
  | "ordine_acquisto_desc" | "ordine_acquisto_asc"
  | "fornitore_desc" | "fornitore_asc"
  | "fornitore_pk_desc" | "fornitore_pk_asc"
  | "status_desc" | "status_asc"
  | "document_type_desc" | "document_type_asc";

export interface DdtListFilters {
  status?: string;
  q?: string;                       // ricerca full-text
  id?: string;
  numero_ddt?: string;
  ordine_acquisto?: string;
  fornitore?: string;
  pdf_filename?: string;
  fornitore_pk?: string;
  document_type?: string;
  /** Filtri di periodo e correzioni: rendono raggiungibili i numeri della pagina statistiche. */
  date_from?: string;
  date_to?: string;
  had_corrections?: boolean;
  /** Campo corretto a mano (percorso con indici normalizzati): drill delle statistiche. */
  corrected_field?: string;
  sort?: DdtSortKey;
  page?: number;
  page_size?: number;
}

export interface DdtCountResponse {
  by_status: Record<string, { count: number; approved_today: number }>;
  total: number;
  pending_review: number;
  approved_today: number;
}

export interface DdtSuggestion {
  id: string;
  status: DdtStatus;
  pdf_filename: string | null;
  numero_ddt: string | null;
  fornitore: string | null;
  fornitore_pk: string | null;
  email_subject: string | null;
  created_at: string | null;
}

/** Risultato lookup fornitore su clienti_fornitori (Item 7, 2026-07-14). */
export interface FornitoreSuggestion {
  codcli_for: string;
  ragsoc: string;
  piva: string | null;
  codice_fiscale: string | null;
  indirizzo: string | null;
  telefono: string | null;
  fax: string | null;
  similarity: number;
}

/** Risultato lookup articolo su `articoli` (punti 2+5, 2026-07-14). */
export interface ArticoloSuggestion {
  codart: string;
  codartalt: string | null;
  descrizione: string | null;
  codaz: string;
  similarity: number;
}

// ── Amministratore HubSpot (Fase 2, 2026-07-15) ─────────────────────────────
// Speculare a GET /requests/amministratori/suggest (src/api/routes/requests.py):
// company HubSpot (tipologia_mappata='AMMINISTRATORE CONDOMINIALE') arricchita
// con gli edifici gestiti localmente. `codice_amministratore` = amministratori.id
// locale (bridge esatto per la coerenza con l'edificio).
export interface AmministratoreEdificioGestito {
  pk: string;              // PK edificio con prefisso cod_az (es. "GRPCP-E001234")
  descrizione: string | null;
  indirizzo: string | null;
  comune: string | null;
}

// ── Magic-link self-service (platform_admin, 2026-07-15) ────────────────────
export interface SelfServiceMagicLink {
  session_id: string;
  code: string;
  magic_url: string;
  expires_in_seconds: number;
}

export interface AmministratoreSuggestion {
  hubspot_id: string;
  name: string;
  partita_iva: string | null;
  codice_fiscale: string | null;
  codice_amministratore: string | null;   // = amministratori.id locale (null se solo-HubSpot)
  city: string | null;
  phone: string | null;
  edifici_gestiti: AmministratoreEdificioGestito[];
  n_edifici: number;
}

// ── Rinascente / La Rinascente — fatture estere ─────────────────────────────────
// Speculare a src/rinascente/models.py (Pydantic) e alla tabella rinascente_invoices.

export type RinascenteTipoDocumento = "fattura" | "credit_memo" | "unknown";

export type RinascenteStatus =
  | "pending"
  | "pending_review"
  | "approved"
  | "rejected"
  | "error"
  | "no_pdf"
  | "not_invoice";

export interface RinascenteRigaArticolo {
  codice_articolo: string | null;
  ean_barcode: string | null;
  hs_code: string | null;
  descrizione: string | null;
  paese_origine: string | null;       // ISO 3166 (FR, DE, CN…)
  quantita: number | null;
  um: string | null;                   // PZ, SET, KG…
  prezzo_unitario: number | null;
  sconto: number | null;
  totale_riga: number | null;
}

export interface RinascenteDocumento {
  tipo: RinascenteTipoDocumento;
  numero: string | null;
  data_emissione: string | null;       // ISO 8601
  data_scadenza: string | null;
  termini_pagamento: string | null;    // NET 60, Net 30 Days…
  incoterms: string | null;            // CPT, EXW, FOB…
}

export interface RinascenteFornitore {
  ragione_sociale: string | null;
  partita_iva: string | null;          // formato per-paese (raw)
  paese: string | null;                // ISO 3166
  indirizzo: string | null;
  codice_oracle: string | null;        // dal filename (anchor)
}

export interface RinascenteCliente {
  ragione_sociale_fatturazione: string | null;   // La Rinascente SPA
  piva_fatturazione: string | null;              // IT05034580968
  ragione_sociale_spedizione: string | null;     // DR Logistics S.R.L.
  indirizzo_spedizione: string | null;
  codice_cliente_interno: string | null;
}

export interface RinascenteTotali {
  valuta: string | null;               // EUR, USD, GBP (ISO 4217)
  imponibile: number | null;
  iva_tasse: number | null;            // 0 per intra-EC
  totale: number | null;               // negativo per credit memo
}

export interface RinascenteOrdineLogistica {
  numero_po: string | null;            // PO 29150, 2026/13744
  tracking: string | null;             // Bill of Lading, DHL tracking
}

export interface RinascenteInvoiceData {
  documento: RinascenteDocumento;
  fornitore: RinascenteFornitore;
  cliente: RinascenteCliente;
  ordine_logistica: RinascenteOrdineLogistica;
  totali: RinascenteTotali;
  righe: RinascenteRigaArticolo[];
}

/** Email originale della fattura (endpoint /email, 404 se assente nel POC). */
export interface RinascenteEmail {
  message_id: string;
  subject: string | null;
  sender: string | null;
  received_at: string | null;
  body_text: string | null;
}

/** Riga completa come ritornata da GET /api/v1/rinascente/{id}. */
export interface RinascenteInvoice {
  id: string;
  tenant_id: string;

  // INPUT
  source_path: string;
  pdf_filename: string | null;
  source_ref: string | null;                 // numero documento dal filename
  codice_fornitore_filename: string | null;  // codice 7 cifre dal filename (anchor Oracle)
  ragsoc_filename: string | null;            // ragione sociale dal filename

  // EMAIL ORIGINALE (null nel POC — pronti per futura mailbox)
  email_message_id: string | null;
  email_subject: string | null;
  email_sender: string | null;
  email_received_at: string | null;
  email_body_text: string | null;

  // PIPELINE OUTPUT
  ocr_text: string | null;
  extracted_data: RinascenteInvoiceData | null;
  llm_metadata: Record<string, unknown> | null;
  completeness: number | null;               // 0.0-1.0
  is_credit_memo: boolean;
  pdf_page_count: number | null;             // numero pagine PDF (per navigazione preview)

  // WORKFLOW
  status: RinascenteStatus;
  error_message: string | null;
  /** path dotted (es. "documento.numero") → valore corretto */
  corrections: Record<string, unknown>;
  final_data: RinascenteInvoiceData | null;

  // OUTPUT (JSON scritto su filesystem ad approve)
  output_path: string | null;
  output_sent_at: string | null;

  // LOCK pessimistico
  locked_by: string | null;
  locked_by_name: string | null;
  locked_at: string | null;

  // AUDIT operatore
  operator_id: string | null;
  operator_name: string | null;
  operator_note: string | null;
  approved_at: string | null;
  rejected_at: string | null;

  created_at: string;
  updated_at: string;
}

/**
 * Riga "slim" della lista (proiezione BE). Alcuni campi sono estratti dal
 * JSONB extracted_data e promossi a colonne top-level per la tabella.
 */
export interface RinascenteInvoiceListItem {
  id: string;
  tenant_id: string;
  status: RinascenteStatus;
  pdf_filename: string | null;
  source_ref: string | null;
  codice_fornitore_filename: string | null;
  ragsoc_filename: string | null;
  is_credit_memo: boolean;
  completeness: number | null;
  pdf_page_count: number | null;
  email_message_id: string | null;
  // Campi promossi da extracted_data:
  numero: string | null;
  data_emissione: string | null;
  tipo_doc: RinascenteTipoDocumento | null;
  fornitore: string | null;
  valuta: string | null;
  totale: string | null;
  locked_by: string | null;
  locked_by_name: string | null;
  locked_at: string | null;
  created_at: string;
  updated_at: string;
  approved_at: string | null;
  rejected_at: string | null;
  operator_id: string | null;
  operator_name: string | null;
  error_message: string | null;
}

export interface RinascenteInvoiceListResponse {
  items: RinascenteInvoiceListItem[];
  total: number;
  page: number;
  page_size: number;
}

export interface RinascenteCountResponse {
  by_status: Record<string, { count: number; approved_today: number }>;
  total: number;
  pending_review: number;
  approved_today: number;
  new_since: number;
}

export interface RinascenteSuggestion {
  id: string;
  status: RinascenteStatus;
  pdf_filename: string | null;
  numero: string | null;
  fornitore: string | null;
  codice_fornitore: string | null;
  created_at: string | null;
}

export type RinascenteSortKey =
  | "date_desc" | "date_asc"
  | "id_desc" | "id_asc"
  | "numero_desc" | "numero_asc"
  | "fornitore_desc" | "fornitore_asc"
  | "totale_desc" | "totale_asc"
  | "status_desc" | "status_asc";

export interface RinascenteListFilters {
  status?: string;
  tipo?: string;                       // "fattura" | "credit_memo"
  q?: string;                          // ricerca full-text
  data_da?: string;                    // data emissione da (YYYY-MM-DD)
  data_a?: string;                     // data emissione a (YYYY-MM-DD)
  sort?: RinascenteSortKey;
  page?: number;
  page_size?: number;
}

// ── Coda Output ──────────────────────────────────────────────────────────────

export interface RinascenteOutputItem {
  id: string;
  tenant_id: string;
  ragsoc_filename: string | null;
  source_ref: string | null;
  source_path: string | null;
  pdf_filename: string | null;
  pdf_page_count: number | null;
  status: string;
  completeness: number;
  final_data: RinascenteInvoiceData | Record<string, unknown> | null;
  output_path: string | null;
  output_sent_at: string | null;
  approved_at: string | null;
  operator_id: string | null;
  operator_name: string | null;
  created_at: string;
  updated_at: string;
  // Campi estratti da final_data per quick-access FE
  numero_documento: string | null;
  data_emissione: string | null;
  tipo_documento: RinascenteTipoDocumento | null;
  ragsoc_fornitore: string | null;
  valuta: string | null;
  totale: number | null;
  n_righe: number;
}

export interface RinascenteOutputListResponse {
  items: RinascenteOutputItem[];
  total: number;
  page: number;
  size: number;
}

export interface RinascenteOutputPurgeResult {
  queue_deleted: number;
  blobs_deleted: number;
  errors: string[];
}

export interface RinascenteReopenResult {
  ok: boolean;
  output_purge: RinascenteOutputPurgeResult;
}

export interface RinascenteClearResult {
  ok: boolean;
  reopened: string[];
  blobs_deleted: number;
  errors: string[];
}

// ── Statistiche ──────────────────────────────────────────────────────────────

export interface RinascenteTopFornitore {
  ragsoc: string;
  count: number;
  avg_completeness: number;
}

export interface RinascenteStatsResponse {
  total: number;
  by_status: Record<string, number>;
  by_date: { date: string; count: number }[];
  top_fornitori: RinascenteTopFornitore[];
  by_completeness: Record<string, number>;
  approved_today: number;
  pending_review: number;
  approved: number;
  rejected: number;
  error: number;
  period_days: number;
}

// ── Configurazione ───────────────────────────────────────────────────────────

export interface RinascenteConfig {
  source_dir: string;
  output_dir: string;
  watcher_interval_seconds: number;
  confidence_threshold_auto_approve: number;
  ocr_max_pdf_bytes: number;
}

export interface RinascenteConfidencePreview {
  total: number;
  would_auto_approve: number;
  would_need_review: number;
  period_days: number;
  threshold: number;
}

// ── Conversazioni (Agente Conversazionale, H4) ──────────────────────────────
// Specchia src/api/routes/conversations.py: nessuna colonna segreta arriva
// mai qui (token/hash/email in chiaro sono già filtrati/mascherati lato API).

export type ConversazioneClassification = "SAFE" | "UNSAFE" | "INCOMPLETE";

export interface ConversazioneListItem {
  id: string;
  tenant_id: string;
  channel: string;
  channel_subject_id: string | null;
  phone_e164: string | null;
  caller_identity_id: string | null;
  status: string;
  agent_config_hash: string | null;
  current_turn_seq: number;
  created_at: string;
  last_activity_at: string | null;
  warning_at: string | null;
  expires_at: string | null;
  closed_at: string | null;
  closed_reason: string | null;
  draft_status: string | null;
  classification: ConversazioneClassification | null;
  reason_codes: string[];
  rdi_generated: number;
  materialized_processing_request_id: string | null;
  verified: boolean;
}

export interface ConversazioniListResponse {
  items: ConversazioneListItem[];
  total: number;
  page: number;
  page_size: number;
}

export interface ConversazioniListFilters {
  status?: string;
  channel?: string;
  verified?: boolean;
  classification?: ConversazioneClassification;
  has_rdi?: boolean;
  reason_code?: string;
  date_from?: string;
  date_to?: string;
  page?: number;
  page_size?: number;
}

export interface ConversazioniCountResponse {
  total: number;
  by_status: Record<string, number>;
  by_classification: Record<string, number>;
}

/** Metrica dichiarata non disponibile: mai un valore, mai uno 0 implicito. */
export type ConversazioneMetric<T> =
  | { available: true; value: T }
  | { available: false; reason: string };

export interface ConversazioniMetrics {
  conversations_started: ConversazioneMetric<number>;
  sessions_completed_with_rdi: ConversazioneMetric<number>;
  drafts_incomplete: ConversazioneMetric<number>;
  explicit_stop: ConversazioneMetric<number>;
  timeout_close: ConversazioneMetric<number>;
  system_uncertain: ConversazioneMetric<number>;
  verification_success_rate: ConversazioneMetric<number>;
  median_turns: ConversazioneMetric<number>;
  avg_turns: ConversazioneMetric<number>;
  median_duration_seconds: ConversazioneMetric<number>;
  avg_duration_seconds: ConversazioneMetric<number>;
  dropoff_by_missing_field: ConversazioneMetric<Record<string, number>>;
  safe_distribution: ConversazioneMetric<Record<string, number>>;
  field_correction_rate: ConversazioneMetric<Record<string, number>>;
  model_token_cost_latency: ConversazioneMetric<never>;
  tool_call_success_failure: ConversazioneMetric<never>;
  turn_latency: ConversazioneMetric<never>;
}

// ── Configurazione Agente Conversazionale (unificata in /settings/agente) ──

export type ConversazioniRequirementClassification = "critical" | "required" | "optional";

export interface ConversazioniRequirement {
  key: string;
  /** Testo descrittivo del requisito. E' il campo reale di `DataRequirement`
   *  lato backend (`str | None`); in produzione e' spesso `null`, per questo
   *  a schermo si ricade su `key`. */
  description: string | null;
  classification: ConversazioniRequirementClassification;
}

export interface ConversazioniLifecycle {
  session_idle_timeout_seconds: number;
  session_max_duration_seconds: number;
  /** null = promemoria disattivato */
  reminder_after_seconds: number | null;
  abandon_after_seconds: number;
  resume_window_seconds: number;
}

export interface ConversazioniDisambiguation {
  max_candidates_shown: number;
  max_disambiguation_rounds: number;
  candidate_query_limit: number;
}

export interface ConversazioniRetention {
  conversation_retention_days: number;
  verification_evidence_retention_days: number;
}

export interface ConversazioniConfig {
  enabled: boolean;
  requirements: ConversazioniRequirement[];
  lifecycle: ConversazioniLifecycle;
  disambiguation: ConversazioniDisambiguation;
  retention: ConversazioniRetention;
  customer_visible_fields: { ticket_fields: string[]; building_fields: string[] };
  /** Blocco di persona/tono, in testa al prompt effettivo — SOLO scrivibile
   *  dal platform_admin (vedi `put_conversation_config`, che lo ignora in
   *  silenzio se lo manda un admin di tenant); leggibile da chiunque apra
   *  questa pagina. `null` = nessuna personalizzazione, si usa il default
   *  Sieve. Le regole non negoziabili (`src/conversation/llm.py`) sono
   *  SEMPRE aggiunte dopo questo testo dal backend e non sono qui.
   *
   *  ATTENZIONE: un tentativo di modifica da parte di chi NON e'
   *  platform_admin ora fallisce con 403 (prima veniva scartato in silenzio e
   *  la PUT rispondeva 200, perdendo il testo scritto senza dirlo). Rispedire
   *  indietro il valore INVARIATO resta lecito: e' cio' che fa questa pagina
   *  ad ogni salvataggio, e non e' una modifica. */
  system_prompt: string | null;
  /** Il modello con cui parla l'agente, come alias prefissato
   *  (`anthropic/claude-...`). `null` = eredita il modello del tenant.
   *  Competenza dell'admin di tenant, NON del platform_admin: e' una scelta
   *  operativa con un prezzo visibile, non una guardia di sicurezza. Solo il
   *  NOME del modello: chiave, endpoint e temperatura restano quelli del
   *  tenant, cosi' scegliere dalla tendina non obbliga a duplicare un
   *  segreto. Un valore fuori dal catalogo prezzi e' rifiutato con 400. */
  llm_model: string | null;
}

/** Una voce della tendina dei modelli (`GET /conversations/models`).
 *  L'elenco viene dal catalogo prezzi, non da una costante nel frontend: un
 *  secondo elenco divergerebbe al primo modello aggiunto, e un modello
 *  selezionabile ma non a catalogo produce costi `NULL` in dashboard. */
export interface ConversazioniSelectableModel {
  value: string;
  name: string;
  input_per_million: number | null;
  output_per_million: number | null;
  /** Gia' formattato dal backend, es. «€4,60 in · €23,00 out». */
  prezzo_leggibile: string;
}

export interface ConversazioniModels {
  /** Ordinati dal piu' economico: la prima voce non e' la piu' cara per caso. */
  models: ConversazioniSelectableModel[];
  /** Il modello che l'agente usa DAVVERO adesso. */
  current: string;
  /** `true` = nessuna scelta per l'agente, si eredita dal tenant (e si
   *  continuera' a seguirlo se il tenant cambia modello). */
  inherited: boolean;
  selected: string | null;
  tenant_model: string;
  /** `false` = il modello in vigore non e' a catalogo, quindi i suoi costi
   *  non sono noti. Succede col modello EREDITATO, che non passa da questa
   *  validazione: va dichiarato, non lasciato dedurre. */
  current_in_catalog: boolean;
}

/** Il prompt di sistema in tre pezzi distinti (`GET /conversations/prompt`).
 *  Separati e non concatenati: e' cio' che permette di mostrare il testo IN
 *  VIGORE senza farlo passare per testo salvato. */
export interface ConversazioniPrompt {
  /** Cio' che c'e' in config: `null` se non personalizzato. */
  saved_persona: string | null;
  /** Il default hardcoded che entra in vigore quando `saved_persona` e' nullo. */
  default_persona: string;
  /** Quello che il modello legge davvero. */
  effective_persona: string;
  is_default: boolean;
  /** Le guardie di prodotto, sempre in coda al prompt. Sola lettura. */
  immutable_rules: string;
  max_length: number;
}

/** Una versione del prompt, con il testo PRIMA e DOPO.
 *  `prompt_before`/`prompt_after` a `null` non sono dati mancanti: significano
 *  «nessun prompt salvato, in vigore il default hardcoded», che e' uno stato
 *  legittimo. I booleani `*_is_default` lo dicono senza far interpretare un
 *  `null`. */
export interface ConversazioniPromptVersion {
  id: string;
  prompt_before: string | null;
  prompt_after: string | null;
  before_is_default: boolean;
  after_is_default: boolean;
  operator_id: string;
  operator_name: string;
  /** Non nullo se questa versione e' nata da un ripristino. */
  restored_from_version_id: string | null;
  created_at: string | null;
}

export interface ConversazioniPromptRestored {
  restored: boolean;
  version_id: string | null;
  reason?: string;
  system_prompt: string | null;
  is_default: boolean;
}

/** Corpo del PUT: stesso oggetto della GET ma senza `enabled` — quello si
 * cambia solo via PATCH .../admin/tenants/{id}/modules (solo platform_admin). */
export type ConversazioniConfigUpdate = Omit<ConversazioniConfig, "enabled">;

export interface ConversazioneDraft {
  id: string;
  ordinal: number;
  status: string;
  classification: ConversazioneClassification | null;
  requirements: Record<string, unknown>;
  evidence: Record<string, unknown>;
  confirmation: Record<string, unknown>;
  reason_codes: string[];
  disambiguation_counts: Record<string, unknown>;
  attachments: unknown[];
  materialized_processing_request_id: string | null;
  materialized_at: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface ConversazioneTranscriptMessage {
  id: string;
  direction: string;
  processing_status: string;
  normalized_text: string | null;
  attachments: unknown[];
  turn_seq: number | null;
  provider_timestamp: string | null;
  received_at: string | null;
  created_at: string | null;
}

export interface ConversazioneTimelineEvent {
  id: string | null;
  type: "audit" | "session_milestone";
  action: string;
  details: Record<string, unknown>;
  at: string | null;
}

export interface ConversazioneVerificationAttempt {
  id: string;
  method: string;
  status: string;
  email_masked: string | null;
  email_domain_class: string | null;
  attempts: number;
  failure_reason: string | null;
  hs_contact_id: string | null;
  created_at: string | null;
  updated_at: string | null;
  consumed_at: string | null;
}

export interface ConversazioneActiveGrant {
  codaz: string;
  codcli_for: string;
  status: string;
  granted_via: string | null;
  granted_at: string | null;
  expires_at: string | null;
}

export interface ConversazioneTechnicalError {
  id: string;
  effect_type: string;
  status: string;
  attempts: number;
  max_attempts: number;
  last_error_class: string | null;
  draft_id: string | null;
  updated_at: string | null;
}

export interface ConversazioneDetail {
  session: {
    id: string;
    tenant_id: string;
    channel: string;
    channel_subject_id: string | null;
    phone_e164: string | null;
    caller_identity_id: string | null;
    status: string;
    current_turn_seq: number;
    created_at: string | null;
    last_activity_at: string | null;
    warning_at: string | null;
    expires_at: string | null;
    closed_at: string | null;
    closed_reason: string | null;
  };
  config_version: {
    agent_config_hash: string | null;
    agent_config_snapshot: Record<string, unknown> | null;
  };
  verification: {
    attempts: ConversazioneVerificationAttempt[];
    active_grant: ConversazioneActiveGrant | null;
  };
  drafts: ConversazioneDraft[];
  rdi_generated: string[];
  transcript: ConversazioneTranscriptMessage[];
  timeline: ConversazioneTimelineEvent[];
  technical_errors: ConversazioneTechnicalError[];
  notes: string[];
}

/** Il draft corrente come lo restituisce `POST /conversations/test-message`:
 *  sottoinsieme di `ConversazioneDraft` con i soli campi che dicono PERCHE'
 *  l'agente ha risposto cosi' (stato, classificazione, reason code) e se la
 *  pratica e' nata. Niente `requirements`/`evidence`: il thread ricaricato
 *  dopo il turno porta il draft completo. */
export interface ConversazioneTestDraft {
  id: string;
  ordinal: number;
  status: string;
  classification: ConversazioneClassification | null;
  reason_codes: string[];
  materialized_processing_request_id: string | null;
  materialized_at: string | null;
}

export interface ConversazioneTestReply {
  session_id: string;
  reply: string | null;
  turns: number;
  session_status: string;
  session_closed_reason: string | null;
  /** NULL finche' la sessione e' `active`: lo scrive il lifecycle solo
   *  passando a `warning`, ed e' la scadenza della GRAZIA. La scadenza per
   *  inattivita' si calcola da `session_last_activity_at`. */
  session_expires_at: string | null;
  session_last_activity_at: string | null;
  /** `true` quando questo turno ha aperto una sessione NUOVA — succede al
   *  primo messaggio e ogni volta che la precedente era scaduta o chiusa.
   *  Senza questo campo il cambio di sessione era invisibile e la pagina
   *  restava agganciata a un `session_id` morto. */
  session_created: boolean;
  draft: ConversazioneTestDraft | null;
}

/** Esito di `POST /conversations/{id}/close` (solo canale `test`). */
export interface ConversazioneCloseReply {
  session_id: string;
  status: string;
  closed_reason: string | null;
  already_closed: boolean;
}

/** Filtri di `GET /requests?only_test_channel=true` — pratiche nate da una
 *  conversazione di prova (`channel='test'`), la lista `/settings/agente/pratiche`.
 *  Stessa forma dei filtri di `fetchAgentRequests`: solo cio' che quella
 *  pagina usa davvero, non l'intero filtro della coda RDI. */
export interface TestChannelRequestsParams {
  status?: string;
  date_from?: string;
  date_to?: string;
  sort?: string;
  page?: number;
  size?: number;
}

// ── GET /conversations/by-request/{request_id} (Pacchetto E) ───────────────
// Alimenta la sezione "Conversazione" in Review3.tsx quando la pratica ha
// origine conversazionale. Sottoinsieme di `ConversazioneDraft` (solo i campi
// che raccontano "cosa e' stato deciso": stato, classificazione, reason
// code, disambiguazioni, conferma) — niente `requirements`/`attachments`,
// non servono a questa vista.

export interface ConversationByRequestDraft {
  id: string;
  session_id: string;
  ordinal: number;
  status: string;
  classification: ConversazioneClassification | null;
  evidence: Record<string, unknown>;
  confirmation: Record<string, unknown>;
  reason_codes: string[];
  disambiguation_counts: Record<string, unknown>;
  materialized_at: string | null;
}

export interface ConversationByRequest {
  request_id?: string;
  media?: { id: string; kind: string; transcript?: string; error?: string }[];
  locations?: { latitude: number; longitude: number; address?: string; name?: string }[];
  session_id: string;
  channel: string;
  draft: ConversationByRequestDraft | null;
  transcript: ConversazioneTranscriptMessage[];
}

// ── Listino prezzi (run `prezzi-listino`, contratto C3) ─────────────────────
// `/api/v1/admin/prices/*`. Un prezzo e' immutabile: si aggiunge una riga
// nuova con validita' temporale, mai si modifica. Il YAML del repo resta la
// baseline (`source: "yaml"`, `id: null`, `ref: "yaml:v1"`); i prezzi decisi
// dall'admin vivono in tabella (`source: "db"`, `ref: "db:<id>"`). Tutto cio'
// che l'API puo' non conoscere e' nullable: in UI diventa "—", mai 0.

/** Unita' di fatturazione del catalogo (`PricingUnit` in `catalog.py`). */
export type PriceUnit = "per_token" | "per_minute" | "per_1k_pages";

/** Provenienza del prezzo corrente: listino del repo o riga in tabella. */
export type PriceSource = "yaml" | "db";

/** Componenti USD per milione di token (solo `unit === "per_token"`). */
export interface PerMillionTokens {
  input?: number | null;
  output?: number | null;
  cache_read?: number | null;
  cache_write?: number | null;
}

/** `PriceRow` serializzato + `ref`. Date ISO 8601 con offset. */
export interface PriceView {
  id: number | null;
  model_id: string;
  unit: PriceUnit | string;
  per_million_tokens: PerMillionTokens | null;
  usd_per_minute: number | null;
  usd_per_1k_pages: number | null;
  batch_multiplier: number | null;
  deployment_multipliers: Record<string, number> | null;
  /** `null` quando la riga e' la voce YAML del catalogo, non una riga DB. */
  valid_from: string | null;
  valid_to: string | null;
  source_url: string | null;
  note: string | null;
  created_by: string | null;
  created_at: string | null;
  ref: string;
}

/** Riga di `GET /admin/prices/models`. */
export interface ModelPriceSummary {
  model_id: string;
  unit: PriceUnit | string;
  current: PriceView | null;
  source: PriceSource;
  history_count: number;
}

/** Body di `POST /admin/prices/models/{model_id}/prices`. */
export interface PriceInput {
  valid_from: string;
  valid_to?: string | null;
  per_million_tokens?: PerMillionTokens | null;
  usd_per_minute?: number | null;
  usd_per_1k_pages?: number | null;
  batch_multiplier?: number | null;
  deployment_multipliers?: Record<string, number> | null;
  source_url?: string | null;
  note?: string | null;
  /** Zero = gratis, non ignoto: va dichiarato esplicitamente. */
  allow_zero?: boolean;
}

/** Parametri di `POST /admin/prices/recalc/preview` (e base dell'apply). */
export interface RecalcRequest {
  model_id?: string | null;
  date_from: string;
  date_to: string;
  tenant_id?: string | null;
}

export interface RecalcDeltaByTenant {
  /** null = chiamate senza cliente nel contesto ("Senza cliente"). */
  tenant_id: string | null;
  delta_usd: number;
}

export interface RecalcNotRecomputable {
  /** Motivo tecnico (es. `quantita_assente`), etichettato in UI. */
  reason: string;
  count: number;
}

/** `RecalcReport` serializzato: l'anteprima non tocca il DB. */
export interface RecalcReportView {
  params: RecalcRequest;
  examined: number;
  would_change: number;
  delta_total_usd: number | null;
  delta_by_tenant: RecalcDeltaByTenant[];
  not_recomputable: RecalcNotRecomputable[];
  /** Hash di parametri + insieme (id, nuovo costo): l'apply lo richiede. */
  fingerprint: string;
}

/** Esito di `POST /admin/prices/recalc/apply`. */
export interface RecalcApplyView {
  changed: number;
  /** Uuid testuale; `null` quando l'apply e' un no-op (nessuna riga cambiata). */
  audit_id: string | null;
  report: RecalcReportView;
}

// ── GET /requests/{request_id}/certifiable-buildings (fase 7) ──────────────
// Alimenta la proposta di certificazione del numero chiamante mostrata in
// Review3 prima dell'approvazione di una pratica conversazionale. Gli edifici
// arrivano dal server (espansione cliente → edifici in scope); `truncated`
// dice che l'elenco è parziale e va detto all'operatore, invece di lasciargli
// credere che sia completo.

export interface CertifiableBuilding {
  codice_edificio: string;
  descrizione?: string | null;
  indirizzo?: string | null;
  comune?: string | null;
}

export interface CertifiableCustomer {
  codaz: string;
  codcli_for: string;
  ragsoc?: string | null;
  /** Primo edificio della pratica, se risolto. Mantenuto per compatibilità. */
  ticket_building: string | null;
  /* TUTTI gli edifici dei ticket della pratica. È questo che il POST concede
     davvero in modalità `ticket_building`: una pratica multi-ticket ne ha più
     d'uno, e l'interfaccia deve annunciarli tutti, altrimenti l'operatore
     approva un'ampiezza diversa da quella che legge. Assente sui backend che
     non lo espongono ancora ⇒ si ricade su `ticket_building`. */
  ticket_buildings?: string[];
  buildings: CertifiableBuilding[];
  /** true ⇒ l'elenco è stato tagliato dal server (limite 200): non è completo. */
  truncated: boolean;
}

export interface CertifiableBuildingsResponse {
  /** Numero del chiamante, sempre dal server: il client non lo inventa mai. */
  phone_e164: string | null;
  already_certified: boolean;
  customers: CertifiableCustomer[];
}

/* Ampiezza della certificazione. Default lato server: "all".
   `tree` e' la modalita' della tabella ad albero (cliente -> edificio ->
   impianto): la selezione e' una **fotografia** dello stato desiderato e puo'
   quindi ridurre, non solo estendere. I tre modi storici restano invariati. */
export type CertifyScopeMode = "all" | "ticket_building" | "selection" | "tree";

/** Riferimento a un cliente, come lo identifica l'anagrafica legacy. */
export interface CertifyCustomerRef {
  codaz: string;
  codcli_for: string;
}

/* Un edificio dentro `tree`.

   `all_assets: true` e `all_assets: false` con la lista di TUTTI gli impianti
   di oggi **non sono la stessa cosa**: il primo e' un grant di edificio e
   comprende anche gli impianti che verranno inseriti domani in anagrafica, il
   secondo congela l'insieme. La differenza si manifesta mesi dopo, quindi va
   portata nel payload, non dedotta dalle spunte. */
export interface CertifyTreeBuilding {
  codice_edificio: string;
  all_assets: boolean;
  /** Richiesto se e solo se `all_assets === false`; mai vuoto (sarebbe una
      revoca dell'edificio, che si esprime togliendo l'edificio). */
  assets?: string[];
}

export interface CertifyTreeCustomer extends CertifyCustomerRef {
  /** true ⇒ nessuna restrizione: tutto il cliente. `buildings` va omesso. */
  all_buildings: boolean;
  /** Con `all_buildings: false` deve contenere almeno un edificio: un cliente
      a zero edifici e' una revoca, e la revoca sta in `revoke`. */
  buildings?: CertifyTreeBuilding[];
}

/** Campo `certify_caller` nel body di POST /requests/{id}/approve. */
export interface CertifyCallerPayload {
  certify: boolean;
  /** Usato dai tre modi storici. Con `scope_mode: "tree"` i clienti sono
      quelli nominati da `tree` e `revoke`, e il campo si omette. */
  customers?: Array<CertifyCustomerRef>;
  scope_mode: CertifyScopeMode;
  /** Richiesto se e solo se scope_mode === "selection"; ignorato con "all". */
  buildings?: string[];
  /** Presente se e solo se scope_mode === "tree". */
  tree?: CertifyTreeCustomer[];
  /* Revoca esplicita del grant su un cliente. Non e' mai dedotta da una lista
     vuota: nel modello "lista vuota" significa **nessuna restrizione**, cioe'
     l'opposto della revoca. */
  revoke?: CertifyCustomerRef[];
  note?: string;
}

// ── GET /requests/{request_id}/certification-tree (run certificazione-albero) ──
// Alimenta la tabella ad albero cliente -> edificio -> impianto di Review3.
// I clienti sono quelli della pratica **piu'** quelli su cui il numero ha gia'
// un grant: l'operatore deve poter governare anche cio' che e' gia' concesso.

/** Cosa e' gia' concesso oggi al numero su quel cliente. */
export interface CertificationGrant {
  /** false ⇒ nessun grant: tutto quello che si spunta e' nuovo. */
  certified: boolean;
  /** true ⇒ grant senza restrizione: tutto il cliente. */
  all_buildings: boolean;
  /** Edifici concessi per intero (impianti futuri compresi). */
  buildings: string[];
  /** Impianti concessi singolarmente. */
  assets: string[];
  /* Codici impianto concessi che l'anagrafica **non conosce piu'**. Non
     autorizzano nulla (fail-closed) e non hanno un edificio a cui essere
     attribuiti: nessuna pagina dell'albero potra' mai contenerli. Sono
     autorizzazioni morte, non impianti "non ancora caricati", e la differenza
     e' sostanziale: trattarli come i secondi bloccherebbe l'approvazione in
     attesa di una riga che non arrivera' mai. */
  unknown_assets?: string[];
}

export interface CertificationTreeBuilding {
  codice_edificio: string;
  descrizione?: string | null;
  indirizzo?: string | null;
  comune?: string | null;
  /** Quanti impianti contiene, per non dover aprire la riga per saperlo. */
  assets_count?: number | null;
  /* Riga **fissata** dal server: arriva sempre, in testa all'elenco, anche
     quando `q`/`offset`/`limit` la escluderebbero, e non e' conteggiata in
     `total_buildings` ne' in `truncated`. E' cosi' che l'operatore non perde
     di vista cio' che sta gia' concedendo, o l'edificio della pratica, mentre
     cerca dentro un cliente da 645 edifici.
     Booleano di comodo: e' derivato da `pinned_reasons`. */
  pinned?: boolean;
  /* **Perche'** la riga e' fissata: `"ticket"` = e' l'edificio di questa
     pratica, `"granted"` = e' gia' concesso a questo numero; entrambi quando i
     due motivi coincidono, lista vuota sulle righe normali della ricerca.
     Sono informazioni diverse per chi decide, e la decisione la prende il
     server, che ha i dati completi: dedurla qui da `ticket_buildings` e da
     `granted` duplicherebbe una scelta gia' fatta altrove, e il giorno in cui
     le due logiche divergono il badge direbbe il falso proprio nel punto in
     cui l'operatore si fida di lui. */
  pinned_reasons?: string[];
  /* Impianti di QUESTO edificio gia' presenti in `granted.assets`, se il
     backend li attribuisce riga per riga. Quando manca, l'attribuzione la fa
     il client aprendo l'edificio, e gli impianti concessi che restano non
     attribuiti vengono dichiarati all'operatore invece di sparire. */
  granted_assets?: string[] | null;
}

export interface CertificationTreeCustomer {
  codaz: string;
  codcli_for: string;
  ragsoc?: string | null;
  /** Edifici dei ticket della pratica: il contesto da cui nasce la chiamata. */
  ticket_buildings?: string[];
  granted: CertificationGrant;
  /** Pagina corrente degli edifici (filtrata da `q`, tagliata da `limit`). */
  buildings: CertificationTreeBuilding[];
  /** Totale reale degli edifici del cliente: `buildings.length` non lo e'. */
  total_buildings: number;
  /** true ⇒ l'elenco e' parziale. Va detto, non nascosto. */
  truncated: boolean;
}

export interface CertificationTreeResponse {
  /** Numero del chiamante, sempre dal server: il client non lo inventa mai. */
  phone_e164: string | null;
  customers: CertificationTreeCustomer[];
}

/* Impianto di un edificio. L'anagrafica **non ha alcun campo di stato**: non
   si puo' sapere se un impianto e' dismesso, e l'interfaccia non deve
   suggerire il contrario. */
export interface CertificationAsset {
  codice_impianto: string;
  desc_tipo?: string | null;
  descrizione?: string | null;
  desc_prest?: string | null;
  potenza?: string | number | null;
}

export interface CertificationAssetsResponse {
  assets: CertificationAsset[];
  /** true ⇒ elenco impianti parziale: una lista esplicita sarebbe incompleta. */
  truncated: boolean;
}
